import React from 'react';
import { getVenueConfig, TEATRO_SANT_ANNA_CONFIG, TEATRO_TORRETTA_CONFIG } from '../data/seatMapConfig';

// Seat status colors
const SEAT_COLORS = {
  available: 'bg-[#25D366] hover:bg-[#128C7E] cursor-pointer',
  selected: 'bg-[#FFD700] hover:bg-[#FFC107] cursor-pointer ring-2 ring-white',
  occupied: 'bg-[#666] cursor-not-allowed opacity-50',
  blocked: 'bg-red-500/50 cursor-not-allowed',
  freed: 'bg-[#25D366] ring-2 ring-blue-400 cursor-pointer',
};

const SeatMap = ({ 
  date, 
  venueId = 'teatro-sant-anna', 
  occupiedSeats = [], 
  selectedSeats = [], 
  onSeatSelect, 
  maxSeats = 10,
  readOnly = false,
  isAdminMode = false,
  freedSeats = []
}) => {
  const config = getVenueConfig(venueId);
  
  const isSeatOccupied = (row, seat) => {
    const seatId = `${row}-${seat}`;
    return occupiedSeats.includes(seatId);
  };
  
  const isSeatSelected = (row, seat) => {
    const seatId = `${row}-${seat}`;
    return selectedSeats.includes(seatId);
  };
  
  const isSeatFreed = (row, seat) => {
    const seatId = `${row}-${seat}`;
    return freedSeats.includes(seatId);
  };
  
  const handleSeatClick = (row, seat) => {
    if (readOnly) return;
    
    const seatId = `${row}-${seat}`;
    
    if (!isAdminMode && isSeatOccupied(row, seat)) return;
    
    if (isSeatSelected(row, seat)) {
      onSeatSelect(selectedSeats.filter(s => s !== seatId));
    } else {
      if (isAdminMode || selectedSeats.length < maxSeats) {
        onSeatSelect([...selectedSeats, seatId]);
      }
    }
  };
  
  const onSeatButtonClick = (e, row, seat) => {
    e.preventDefault();
    e.stopPropagation();
    handleSeatClick(row, seat);
  };
  
  const getSeatStatus = (row, seat) => {
    if (isSeatSelected(row, seat)) return 'selected';
    if (isSeatFreed(row, seat)) return 'freed';
    if (isSeatOccupied(row, seat)) return 'occupied';
    return 'available';
  };

  // Render a single seat button
  const renderSeat = (row, seatNumber, key) => {
    const status = getSeatStatus(row, seatNumber);
    const isClickable = isAdminMode || (status !== 'occupied' && status !== 'blocked');
    
    return (
      <button
        type="button"
        key={key}
        onClick={(e) => onSeatButtonClick(e, row, seatNumber)}
        disabled={!isClickable || readOnly}
        className={`
          w-5 h-5 md:w-6 md:h-6 rounded-t-md text-[8px] md:text-[10px] font-medium
          transition-all duration-150 flex items-center justify-center
          ${SEAT_COLORS[status]}
          ${isClickable && !readOnly ? 'hover:scale-110' : ''}
          ${isAdminMode && status === 'occupied' ? 'cursor-pointer hover:ring-2 hover:ring-red-500' : ''}
        `}
        title={`Fila ${row}, Posto ${seatNumber}${status === 'occupied' && isAdminMode ? ' (clicca per liberare)' : ''}`}
        data-testid={`seat-${row}-${seatNumber}`}
      >
        {seatNumber}
      </button>
    );
  };

  // Render Teatro Sant'Anna layout (simple rows)
  const renderSantAnnaLayout = () => (
    <div className="space-y-1 overflow-x-auto pb-4">
      {config.rows.map((rowConfig) => (
        <div key={rowConfig.row} className="flex items-center justify-center gap-0.5">
          <div className="w-8 text-right pr-1 text-white/60 text-[10px] font-medium shrink-0">
            {rowConfig.row}
          </div>
          
          <div className="flex gap-0.5 justify-center">
            {Array.from({ length: rowConfig.seats }, (_, i) => {
              const seatNumber = rowConfig.seats - i;
              return renderSeat(rowConfig.row, seatNumber, `${rowConfig.row}-${seatNumber}`);
            })}
          </div>
          
          <div className="w-8 text-left pl-1 text-white/60 text-[10px] font-medium shrink-0">
            {rowConfig.row}
          </div>
        </div>
      ))}
    </div>
  );

  // Render Teatro Torretta layout (with center aisle)
  const renderTorrettaLayout = () => (
    <div className="space-y-1 overflow-x-auto pb-4">
      {config.rows.map((rowConfig) => (
        <div key={rowConfig.row} className="flex items-center justify-center gap-0.5">
          <div className="w-8 text-right pr-1 text-white/60 text-[10px] font-medium shrink-0">
            {rowConfig.row}
          </div>
          
          {/* Left block */}
          <div className="flex gap-0.5">
            {rowConfig.leftSeats.map((seatNum) => 
              renderSeat(rowConfig.row, seatNum, `${rowConfig.row}-${seatNum}`)
            )}
          </div>
          
          {/* Center aisle */}
          <div className="w-4 md:w-6"></div>
          
          {/* Right block */}
          <div className="flex gap-0.5">
            {rowConfig.rightSeats.map((seatNum) => 
              renderSeat(rowConfig.row, seatNum, `${rowConfig.row}-${seatNum}`)
            )}
          </div>
          
          <div className="w-8 text-left pl-1 text-white/60 text-[10px] font-medium shrink-0">
            {rowConfig.row}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full">
      {/* Stage */}
      <div className="mb-4">
        <div className="bg-gradient-to-b from-[#E91E63] to-[#9C27B0] text-white text-center py-2 rounded-t-xl font-bold text-sm">
          PALCO
        </div>
        <div className="h-1 bg-gradient-to-b from-[#9C27B0]/50 to-transparent"></div>
      </div>
      
      {/* Seats Grid - Different layout based on venue */}
      {config.hasAisle ? renderTorrettaLayout() : renderSantAnnaLayout()}
      
      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-3 mt-4 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-t-md bg-[#25D366]"></div>
          <span className="text-white/70">Disponibile</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-t-md bg-[#FFD700]"></div>
          <span className="text-white/70">Selezionato</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-4 h-4 rounded-t-md bg-[#666] opacity-50"></div>
          <span className="text-white/70">Occupato</span>
        </div>
      </div>
      
      {/* Selected seats summary */}
      {selectedSeats.length > 0 && !readOnly && (
        <div className="mt-3 p-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-xl">
          <p className="text-[#FFD700] text-xs font-medium text-center">
            Posti selezionati: {selectedSeats.length}
          </p>
          <p className="text-white/60 text-[10px] text-center mt-1">
            {selectedSeats.map(s => {
              const [row, seat] = s.split('-');
              return `F${row} P${seat}`;
            }).join(' • ')}
          </p>
        </div>
      )}
    </div>
  );
};

export default SeatMap;
